import { Button, Icon, TextInput, TextLink } from "@components/library";
import { COLORS, FONTS, WINDOW_DIMENSIONS } from "@constants";
import { toggleLoginModal } from "@redux/actions/profileActions";
import { SegmentEventName } from "@tsTypes/__generated__/enums";
import { track } from "@utils/appUtils";
import {
  setPrevUrlCookie,
  setSignupEntryCookie,
  setSignupPrefillEmailCookie,
} from "@utils/authUtils";
import { t } from "@utils/i18n";
import { useState } from "react";
import { useDispatch } from "react-redux";
import styled from "styled-components";

export default function ProgressiveGate({ companyName }: { companyName: string | undefined }) {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const handleSubmit = () => {
    setSignupPrefillEmailCookie(email);
    setPrevUrlCookie(window.location.href);
    setSignupEntryCookie("request_gate");
    track(SegmentEventName.CLICK, {
      ui_component: "Create Account Button - Request Gate",
      email,
    });
    dispatch(toggleLoginModal({ open: true, initialView: "signup" }));
  };

  return (
    <Container>
      <Content>
        <Header>
          <Icon name="Lock" size="md" />
          <Title>{t("pages.progressive_gate.title")}</Title>
          <Subtitle>
            {t("pages.progressive_gate.subtitle", {
              company_name: companyName ?? t("pages.progressive_gate.private_company"),
            })}
          </Subtitle>
        </Header>

        <Form
          as="form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmit();
          }}
        >
          <EmailInputWrapper>
            <TextInput
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder={t("pages.progressive_gate.email_placeholder")}
              hideClearButton
            />
          </EmailInputWrapper>
          <SubmitButton variant="marketing" withDropShadow type="submit" onClick={handleSubmit}>
            {t("pages.progressive_gate.cta_create_account")}
          </SubmitButton>
        </Form>
        <Signin>
          {t("pages.progressive_gate.already_have_account")}{" "}
          <TextLink
            to="#"
            color={COLORS.BLUE_500}
            underline={true}
            onClick={() => {
              setPrevUrlCookie(window.location.href);
              dispatch(
                toggleLoginModal({
                  open: true,
                  initialView: "login",
                })
              );
            }}
          >
            {t("pages.progressive_gate.sign_in")}
          </TextLink>
        </Signin>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  position: relative;
  border: 1px solid ${COLORS.BLUE_LIGHT_500};
  border-radius: 12px;
  padding: 32px;
  background: ${COLORS.BLUE_LIGHT_100};
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: stretch;
  max-width: 520px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const Title = styled.div`
  ${FONTS.HEADING_5_REGULAR};
  text-align: center;
`;
const Subtitle = styled.div`
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_500};
  text-align: center;
`;

const Form = styled.div`
  display: flex;
  width: 100%;
  margin-top: 24px;
  gap: 8px;
  align-items: stretch;
  max-width: 450px;

  ${WINDOW_DIMENSIONS.MOBILE_MEDIA_QUERY} {
    flex-direction: column;
  }
`;

const Signin = styled.div`
  ${FONTS.REGULAR_2};
  color: ${COLORS.NEUTRAL_600};
  margin-top: 24px;
`;

const EmailInputWrapper = styled.div`
  flex: 1;
  input {
    height: 42px;
  }
`;

const SubmitButton = styled(Button)`
  flex-shrink: 0;
  padding-left: 24px;
  padding-right: 24px;
`;
